// AIInsightsDashboard.jsx - Comprehensive Kubi AI Insights Dashboard
// Modern, beautiful design with full functionality and animations

import React, { useState, useEffect, useCallback, useRef } from 'react';

// ============================================================================
// CONSTANTS & CONFIG
// ============================================================================

const API_BASE = process.env.REACT_APP_API_URL || 'https://kubi-backend-production.up.railway.app';

// ============================================================================
// API HELPER
// ============================================================================

const apiCall = async (endpoint, options = {}) => {
  const token = localStorage.getItem('kubi_token');
  const response = await fetch(`${API_BASE}${endpoint}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
      ...options.headers,
    },
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({ error: 'Request failed' }));
    throw new Error(err.error || `HTTP ${response.status}`);
  }
  return response.json();
};

// ============================================================================
// THEME HOOK
// ============================================================================

const useThemeColors = () => {
  const [isDark, setIsDark] = useState(() => {
    const stored = localStorage.getItem('kubi_darkMode');
    if (stored !== null) return stored !== 'false';
    return true;
  });

  useEffect(() => {
    const handleStorage = () => {
      const stored = localStorage.getItem('kubi_darkMode');
      if (stored !== null) setIsDark(stored !== 'false');
    };
    window.addEventListener('storage', handleStorage);
    const interval = setInterval(handleStorage, 500);
    return () => {
      window.removeEventListener('storage', handleStorage);
      clearInterval(interval);
    };
  }, []);

  return isDark ? {
    bg: '#0f172a',
    bgCard: '#1e293b',
    bgCardHover: '#334155',
    bgElevated: '#1e293b',
    bgInput: '#0f172a',
    text: '#f8fafc',
    textSecondary: '#94a3b8',
    textMuted: '#64748b',
    border: 'rgba(255, 255, 255, 0.1)',
    borderLight: 'rgba(255, 255, 255, 0.05)',
    primary: '#0a66c2',
    primaryHover: '#0856a8',
    primaryGlow: 'rgba(10, 102, 194, 0.3)',
    success: '#10b981',
    successGlow: 'rgba(16, 185, 129, 0.3)',
    warning: '#eab308',
    warningGlow: 'rgba(234, 179, 8, 0.3)',
    error: '#ef4444',
    errorGlow: 'rgba(239, 68, 68, 0.3)',
    info: '#3b82f6',
    purple: '#1E88E5',
    purpleGlow: 'rgba(30, 136, 229, 0.3)',
    orange: '#fb923c',
    orangeGlow: 'rgba(251, 146, 60, 0.3)',
    gradient1: 'linear-gradient(135deg, #0a66c2 0%, #1E88E5 100%)',
    gradient2: 'linear-gradient(135deg, #fb923c 0%, #ef4444 100%)',
    isDark: true
  } : {
    bg: '#f8fafc',
    bgCard: '#ffffff',
    bgCardHover: '#f1f5f9',
    bgElevated: '#ffffff',
    bgInput: '#f1f5f9',
    text: '#0f172a',
    textSecondary: '#475569',
    textMuted: '#94a3b8',
    border: 'rgba(0, 0, 0, 0.1)',
    borderLight: 'rgba(0, 0, 0, 0.05)',
    primary: '#0a66c2',
    primaryHover: '#0856a8',
    primaryGlow: 'rgba(10, 102, 194, 0.15)',
    success: '#059669',
    successGlow: 'rgba(5, 150, 105, 0.15)',
    warning: '#ca8a04',
    warningGlow: 'rgba(202, 138, 4, 0.15)',
    error: '#dc2626',
    errorGlow: 'rgba(220, 38, 38, 0.15)',
    info: '#2563eb',
    purple: '#1565C0',
    purpleGlow: 'rgba(21, 101, 192, 0.15)',
    orange: '#f97316',
    orangeGlow: 'rgba(249, 115, 22, 0.15)',
    gradient1: 'linear-gradient(135deg, #0a66c2 0%, #1565C0 100%)',
    gradient2: 'linear-gradient(135deg, #f97316 0%, #dc2626 100%)',
    isDark: false
  };
};

// ============================================================================
// ICONS (Lucide-style SVGs)
// ============================================================================

const Icon = ({ name, size = 20, color = 'currentColor', className = '', style = {} }) => {
  const icons = {
    sparkles: <><path d="M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .963 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.581a.5.5 0 0 1 0 .964L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.963 0z"/><path d="M20 3v4"/><path d="M22 5h-4"/><path d="M4 17v2"/><path d="M5 18H3"/></>,
    brain: <><path d="M12 5a3 3 0 1 0-5.997.125 4 4 0 0 0-2.526 5.77 4 4 0 0 0 .556 6.588A4 4 0 1 0 12 18Z"/><path d="M12 5a3 3 0 1 1 5.997.125 4 4 0 0 1 2.526 5.77 4 4 0 0 1-.556 6.588A4 4 0 1 1 12 18Z"/><path d="M15 13a4.5 4.5 0 0 1-3-4 4.5 4.5 0 0 1-3 4"/><path d="M17.599 6.5a3 3 0 0 0 .399-1.375"/><path d="M6.003 5.125A3 3 0 0 0 6.401 6.5"/><path d="M3.477 10.896a4 4 0 0 1 .585-.396"/><path d="M19.938 10.5a4 4 0 0 1 .585.396"/><path d="M6 18a4 4 0 0 1-1.967-.516"/><path d="M19.967 17.484A4 4 0 0 1 18 18"/></>,
    target: <><circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="6"/><circle cx="12" cy="12" r="2"/></>,
    users: <><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></>,
    trendingUp: <><polyline points="22 7 13.5 15.5 8.5 10.5 2 17"/><polyline points="16 7 22 7 22 13"/></>,
    clock: <><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></>,
    alertCircle: <><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></>,
    checkCircle: <><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></>,
    check: <><polyline points="20 6 9 17 4 12"/></>,
    phone: <><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/></>,
    mail: <><rect width="20" height="16" x="2" y="4" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/></>,
    send: <><path d="m22 2-7 20-4-9-9-4Z"/><path d="M22 2 11 13"/></>,
    messageSquare: <><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></>,
    calendar: <><rect width="18" height="18" x="3" y="4" rx="2" ry="2"/><line x1="16" x2="16" y1="2" y2="6"/><line x1="8" x2="8" y1="2" y2="6"/><line x1="3" x2="21" y1="10" y2="10"/></>,
    zap: <><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></>,
    briefcase: <><rect width="20" height="14" x="2" y="7" rx="2" ry="2"/><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/></>,
    dollarSign: <><line x1="12" x2="12" y1="2" y2="22"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></>,
    arrowRight: <><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></>,
    arrowUpRight: <><path d="M7 17 17 7"/><path d="M7 7h10v10"/></>,
    refresh: <><path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8"/><path d="M21 3v5h-5"/><path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16"/><path d="M8 16H3v5"/></>,
    star: <><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></>,
    lightbulb: <><path d="M15 14c.2-1 .7-1.7 1.5-2.5 1-.9 1.5-2.2 1.5-3.5A6 6 0 0 0 6 8c0 1 .2 2.2 1.5 3.5.7.7 1.3 1.5 1.5 2.5"/><path d="M9 18h6"/><path d="M10 22h4"/></>,
    pieChart: <><path d="M21.21 15.89A10 10 0 1 1 8 2.83"/><path d="M22 12A10 10 0 0 0 12 2v10z"/></>,
    activity: <><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></>,
    userPlus: <><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><line x1="19" x2="19" y1="8" y2="14"/><line x1="22" x2="16" y1="11" y2="11"/></>,
    building: <><rect width="16" height="20" x="4" y="2" rx="2" ry="2"/><path d="M9 22v-4h6v4"/><path d="M8 6h.01"/><path d="M16 6h.01"/><path d="M12 6h.01"/><path d="M12 10h.01"/><path d="M12 14h.01"/><path d="M16 10h.01"/><path d="M16 14h.01"/><path d="M8 10h.01"/><path d="M8 14h.01"/></>,
    externalLink: <><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" x2="21" y1="14" y2="3"/></>,
    chevronRight: <><path d="m9 18 6-6-6-6"/></>,
    chevronDown: <><path d="m6 9 6 6 6-6"/></>,
    x: <><path d="M18 6 6 18"/><path d="m6 6 12 12"/></>,
    settings: <><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"/></>,
    bell: <><path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9"/><path d="M10.3 21a1.94 1.94 0 0 0 3.4 0"/></>,
    loader: <><line x1="12" x2="12" y1="2" y2="6"/><line x1="12" x2="12" y1="18" y2="22"/><line x1="4.93" x2="7.76" y1="4.93" y2="7.76"/><line x1="16.24" x2="19.07" y1="16.24" y2="19.07"/><line x1="2" x2="6" y1="12" y2="12"/><line x1="18" x2="22" y1="12" y2="12"/><line x1="4.93" x2="7.76" y1="19.07" y2="16.24"/><line x1="16.24" x2="19.07" y1="7.76" y2="4.93"/></>,
    fire: <><path d="M8.5 14.5A2.5 2.5 0 0 0 11 12c0-1.38-.5-2-1-3-1.072-2.143-.224-4.054 2-6 .5 2.5 2 4.9 4 6.5 2 1.6 3 3.5 3 5.5a7 7 0 1 1-14 0c0-1.153.433-2.294 1-3a2.5 2.5 0 0 0 2.5 2.5z"/></>,
    rocket: <><path d="M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 0 0-2.91-.09z"/><path d="m12 15-3-3a22 22 0 0 1 2-3.95A12.88 12.88 0 0 1 22 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 0 1-4 2z"/><path d="M9 12H4s.55-3.03 2-4c1.62-1.08 5 0 5 0"/><path d="M12 15v5s3.03-.55 4-2c1.08-1.62 0-5 0-5"/></>,
    award: <><circle cx="12" cy="8" r="6"/><path d="M15.477 12.89 17 22l-5-3-5 3 1.523-9.11"/></>,
    trophy: <><path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6"/><path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18"/><path d="M4 22h16"/><path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22"/><path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22"/><path d="M18 2H6v7a6 6 0 0 0 12 0V2Z"/></>,
    compass: <><circle cx="12" cy="12" r="10"/><polygon points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88 16.24 7.76"/></>,
  };

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      style={style}
    >
      {icons[name] || null}
    </svg>
  );
};

// ============================================================================
// INJECT STYLES
// ============================================================================

const injectStyles = () => {
  if (typeof document === 'undefined' || document.getElementById('ai-insights-styles')) return;
  const styles = document.createElement('style');
  styles.id = 'ai-insights-styles';
  styles.textContent = `
    @keyframes fadeInUp {
      from { opacity: 0; transform: translateY(20px); }
      to { opacity: 1; transform: translateY(0); }
    }
    @keyframes fadeIn {
      from { opacity: 0; }
      to { opacity: 1; }
    }
    @keyframes slideInRight {
      from { opacity: 0; transform: translateX(20px); }
      to { opacity: 1; transform: translateX(0); }
    }
    @keyframes pulse {
      0%, 100% { opacity: 1; }
      50% { opacity: 0.5; }
    }
    @keyframes shimmer {
      0% { background-position: -200% 0; }
      100% { background-position: 200% 0; }
    }
    @keyframes spin {
      from { transform: rotate(0deg); }
      to { transform: rotate(360deg); }
    }
    @keyframes bounce {
      0%, 100% { transform: translateY(0); }
      50% { transform: translateY(-4px); }
    }
    @keyframes glow {
      0%, 100% { box-shadow: 0 0 0 0 currentColor; }
      50% { box-shadow: 0 0 20px 0 currentColor; }
    }
    .ai-fade-in { animation: fadeInUp 0.5s ease-out forwards; opacity: 0; }
    .ai-fade-in-fast { animation: fadeIn 0.3s ease-out forwards; }
    .ai-slide-in { animation: slideInRight 0.4s ease-out forwards; opacity: 0; }
    .ai-stagger-1 { animation-delay: 0.05s; }
    .ai-stagger-2 { animation-delay: 0.1s; }
    .ai-stagger-3 { animation-delay: 0.15s; }
    .ai-stagger-4 { animation-delay: 0.2s; }
    .ai-stagger-5 { animation-delay: 0.25s; }
    .ai-pulse { animation: pulse 2s ease-in-out infinite; }
    .ai-spin { animation: spin 1s linear infinite; }
    .ai-bounce { animation: bounce 1s ease-in-out infinite; }
    .ai-shimmer {
      background: linear-gradient(90deg, transparent 25%, rgba(255,255,255,0.1) 50%, transparent 75%);
      background-size: 200% 100%;
      animation: shimmer 1.5s infinite;
    }
    .ai-card {
      transition: all 0.2s ease;
    }
    .ai-card:hover {
      transform: translateY(-2px);
    }
    .ai-glow-primary:hover { box-shadow: 0 0 30px rgba(10, 102, 194, 0.3); }
    .ai-glow-purple:hover { box-shadow: 0 0 30px rgba(30, 136, 229, 0.3); }
    .ai-glow-orange:hover { box-shadow: 0 0 30px rgba(249, 115, 22, 0.3); }
    .ai-glow-success:hover { box-shadow: 0 0 30px rgba(16, 185, 129, 0.3); }
    .ai-scrollbar::-webkit-scrollbar { width: 6px; height: 6px; }
    .ai-scrollbar::-webkit-scrollbar-track { background: transparent; }
    .ai-scrollbar::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.2); border-radius: 3px; }
    .ai-scrollbar::-webkit-scrollbar-thumb:hover { background: rgba(255,255,255,0.3); }
    .ai-typing-dot {
      animation: bounce 1.4s ease-in-out infinite;
    }
    .ai-typing-dot:nth-child(1) { animation-delay: 0s; }
    .ai-typing-dot:nth-child(2) { animation-delay: 0.2s; }
    .ai-typing-dot:nth-child(3) { animation-delay: 0.4s; }
    .ai-gradient-text {
      background: linear-gradient(135deg, #0a66c2, #1E88E5);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;
    }
    .ai-gradient-border {
      position: relative;
    }
    .ai-gradient-border::before {
      content: '';
      position: absolute;
      inset: 0;
      border-radius: inherit;
      padding: 1px;
      background: linear-gradient(135deg, #0a66c2, #1E88E5);
      -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
      mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
      -webkit-mask-composite: xor;
      mask-composite: exclude;
      pointer-events: none;
    }
  `;
  document.head.appendChild(styles);
};

// ============================================================================
// COMPONENTS
// ============================================================================

const Card = ({ children, style = {}, className = '', onClick, glow }) => {
  const colors = useThemeColors();
  const glowClass = glow ? `ai-glow-${glow}` : '';
  return (
    <div
      onClick={onClick}
      className={`ai-card ${glowClass} ${className}`}
      style={{
        background: colors.bgCard,
        borderRadius: 16,
        border: `1px solid ${colors.border}`,
        overflow: 'hidden',
        cursor: onClick ? 'pointer' : 'default',
        ...style,
      }}
    >
      {children}
    </div>
  );
};

const Badge = ({ children, variant = 'default', size = 'sm', pulse }) => {
  const colors = useThemeColors();
  const variants = {
    default: { bg: colors.isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.06)', color: colors.textSecondary },
    primary: { bg: 'rgba(10, 102, 194, 0.15)', color: colors.primary },
    success: { bg: 'rgba(16, 185, 129, 0.15)', color: colors.success },
    warning: { bg: 'rgba(245, 158, 11, 0.15)', color: colors.warning },
    error: { bg: 'rgba(239, 68, 68, 0.15)', color: colors.error },
    purple: { bg: 'rgba(30, 136, 229, 0.15)', color: colors.purple },
    orange: { bg: 'rgba(249, 115, 22, 0.15)', color: colors.orange },
  };
  const v = variants[variant] || variants.default;
  const padding = size === 'sm' ? '4px 10px' : size === 'xs' ? '2px 6px' : '6px 14px';
  const fontSize = size === 'sm' ? 11 : size === 'xs' ? 10 : 12;

  return (
    <span className={pulse ? 'ai-pulse' : ''} style={{
      display: 'inline-flex',
      alignItems: 'center',
      padding,
      borderRadius: 20,
      fontSize,
      fontWeight: 600,
      background: v.bg,
      color: v.color,
      textTransform: 'uppercase',
      letterSpacing: '0.03em',
      whiteSpace: 'nowrap',
    }}>
      {children}
    </span>
  );
};

const Button = ({ children, variant = 'primary', size = 'md', icon, loading, disabled, onClick, style = {} }) => {
  const colors = useThemeColors();
  const variants = {
    primary: { bg: colors.primary, color: '#fff', hover: colors.primaryHover },
    secondary: { bg: colors.isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.06)', color: colors.text, hover: colors.isDark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.1)' },
    ghost: { bg: 'transparent', color: colors.textSecondary, hover: colors.isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.06)' },
    success: { bg: colors.success, color: '#fff', hover: colors.isDark ? '#059669' : '#047857' },
    warning: { bg: colors.warning, color: '#fff', hover: colors.isDark ? '#d97706' : '#b45309' },
    danger: { bg: colors.error, color: '#fff', hover: colors.isDark ? '#dc2626' : '#b91c1c' },
  };
  const v = variants[variant] || variants.primary;
  const padding = size === 'sm' ? '8px 14px' : size === 'lg' ? '14px 24px' : '10px 18px';
  const fontSize = size === 'sm' ? 13 : size === 'lg' ? 15 : 14;

  return (
    <button
      onClick={onClick}
      disabled={disabled || loading}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 8,
        padding,
        borderRadius: 10,
        border: 'none',
        background: disabled ? colors.isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)' : v.bg,
        color: disabled ? colors.textMuted : v.color,
        fontSize,
        fontWeight: 600,
        cursor: disabled ? 'not-allowed' : 'pointer',
        transition: 'all 0.15s ease',
        opacity: loading ? 0.8 : 1,
        ...style,
      }}
      onMouseOver={(e) => !disabled && (e.currentTarget.style.background = v.hover)}
      onMouseOut={(e) => !disabled && (e.currentTarget.style.background = v.bg)}
    >
      {loading ? <Icon name="loader" size={size === 'sm' ? 14 : 16} className="ai-spin" /> : icon && <Icon name={icon} size={size === 'sm' ? 14 : 16} />}
      {children}
    </button>
  );
};

const Avatar = ({ name, size = 40, src }) => {
  const colors = useThemeColors();
  const initials = name?.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase() || '?';

  if (src) {
    return (
      <img
        src={src}
        alt={name}
        style={{
          width: size,
          height: size,
          borderRadius: size / 2,
          objectFit: 'cover',
        }}
      />
    );
  }

  return (
    <div style={{
      width: size,
      height: size,
      borderRadius: size / 2,
      background: colors.gradient1,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: '#fff',
      fontSize: size * 0.4,
      fontWeight: 600,
      flexShrink: 0,
    }}>
      {initials}
    </div>
  );
};

const MetricCard = ({ icon, label, value, subValue, trend, color = 'primary', className = '' }) => {
  const colors = useThemeColors();
  const accentColor = colors[color] || colors.primary;

  return (
    <Card className={className} glow={color} style={{ padding: 20 }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14 }}>
        <div style={{
          width: 48,
          height: 48,
          borderRadius: 12,
          background: `${accentColor}15`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}>
          <Icon name={icon} size={24} color={accentColor} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 12, color: colors.textMuted, marginBottom: 6, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.03em' }}>{label}</div>
          <div style={{ fontSize: 28, fontWeight: 700, color: colors.text, lineHeight: 1 }}>{value}</div>
          {subValue && (
            <div style={{ fontSize: 13, color: colors.textSecondary, marginTop: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
              {trend !== undefined && (
                <span style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 2,
                  color: trend > 0 ? colors.success : trend < 0 ? colors.error : colors.textMuted,
                  fontWeight: 600,
                }}>
                  <Icon name={trend > 0 ? 'trendingUp' : trend < 0 ? 'trendingUp' : 'arrowRight'} size={14} style={trend < 0 ? { transform: 'scaleY(-1)' } : {}} />
                  {trend > 0 ? '+' : ''}{trend}%
                </span>
              )}
              <span>{subValue}</span>
            </div>
          )}
        </div>
      </div>
    </Card>
  );
};

const PriorityCard = ({ priority, onAction, onComplete }) => {
  const colors = useThemeColors();
  const priorityConfig = {
    critical: { color: colors.error, glow: 'rgba(239, 68, 68, 0.2)', badge: 'error', icon: 'fire' },
    high: { color: colors.warning, glow: 'rgba(245, 158, 11, 0.2)', badge: 'warning', icon: 'zap' },
    medium: { color: colors.info, glow: 'rgba(59, 130, 246, 0.2)', badge: 'primary', icon: 'target' },
    low: { color: colors.textMuted, glow: 'transparent', badge: 'default', icon: 'clock' },
  };
  const config = priorityConfig[priority.priority] || priorityConfig.medium;
  const typeIcons = {
    contact: 'phone',
    candidate: 'users',
    pipeline: 'activity',
    opportunity: 'briefcase',
    revenue: 'dollarSign',
    deadline: 'calendar',
    market: 'trendingUp',
  };

  return (
    <Card style={{
      borderLeft: `4px solid ${config.color}`,
      background: colors.isDark ? `linear-gradient(135deg, ${colors.bgCard} 0%, ${config.glow} 100%)` : colors.bgCard,
    }}>
      <div style={{ padding: 18 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14 }}>
          <div style={{
            width: 40,
            height: 40,
            borderRadius: 10,
            background: `${config.color}20`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}>
            <Icon name={typeIcons[priority.type] || config.icon} size={20} color={config.color} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
              <Badge variant={config.badge} size="xs" pulse={priority.priority === 'critical'}>{priority.priority}</Badge>
              {priority.category && <Badge size="xs">{priority.category}</Badge>}
              {priority.timeEstimate && <Badge size="xs" variant="purple">{priority.timeEstimate}</Badge>}
            </div>
            <h4 style={{ margin: '0 0 6px', fontSize: 15, fontWeight: 600, color: colors.text, lineHeight: 1.4 }}>
              {priority.title}
            </h4>
            <p style={{ margin: 0, fontSize: 13, color: colors.textSecondary, lineHeight: 1.5 }}>
              {priority.description || priority.reason}
            </p>
          </div>
          {priority.value && (
            <div style={{ textAlign: 'right', flexShrink: 0 }}>
              <div style={{ fontSize: 20, fontWeight: 700, color: config.color }}>{priority.value}</div>
              {priority.valueLabel && <div style={{ fontSize: 11, color: colors.textMuted }}>{priority.valueLabel}</div>}
            </div>
          )}
        </div>
        {(priority.action || onComplete) && (
          <div style={{ display: 'flex', gap: 10, marginTop: 14, paddingTop: 14, borderTop: `1px solid ${colors.border}` }}>
            {priority.action && (
              <Button size="sm" icon="arrowRight" onClick={() => onAction?.(priority)}>
                {priority.action}
              </Button>
            )}
            {onComplete && (
              <Button size="sm" variant="ghost" icon="check" onClick={() => onComplete?.(priority)}>
                Done
              </Button>
            )}
          </div>
        )}
      </div>
    </Card>
  );
};

const ContactCard = ({ contact, onAction }) => {
  const colors = useThemeColors();
  const urgencyConfig = {
    critical: { color: colors.error, badge: 'error', pulse: true },
    urgent: { color: colors.error, badge: 'error', pulse: true },
    high: { color: colors.warning, badge: 'warning', pulse: false },
    normal: { color: colors.primary, badge: 'primary', pulse: false },
    low: { color: colors.textMuted, badge: 'default', pulse: false },
  };
  const config = urgencyConfig[contact.urgency] || urgencyConfig.normal;

  return (
    <Card style={{ padding: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
        <Avatar name={contact.name} size={48} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
            <h4 style={{ margin: 0, fontSize: 15, fontWeight: 600, color: colors.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {contact.name}
            </h4>
            <div style={{
              width: 8,
              height: 8,
              borderRadius: 4,
              background: config.color,
              flexShrink: 0,
            }} className={config.pulse ? 'ai-pulse' : ''} />
          </div>
          <div style={{ fontSize: 13, color: colors.textSecondary }}>{contact.title || contact.role}</div>
          <div style={{ fontSize: 12, color: colors.textMuted, marginTop: 4, display: 'flex', alignItems: 'center', gap: 4 }}>
            <Icon name="lightbulb" size={12} />
            {contact.reason}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
          {contact.phone && (
            <Button size="sm" variant="ghost" icon="phone" onClick={() => onAction?.('call', contact)} style={{ padding: '8px 10px' }} />
          )}
          {contact.email && (
            <Button size="sm" variant="ghost" icon="mail" onClick={() => onAction?.('email', contact)} style={{ padding: '8px 10px' }} />
          )}
        </div>
      </div>
    </Card>
  );
};

const AlertCard = ({ alert, onDismiss }) => {
  const colors = useThemeColors();
  const severityConfig = {
    critical: { color: colors.error, icon: 'alertCircle', bg: 'rgba(239, 68, 68, 0.1)' },
    warning: { color: colors.warning, icon: 'alertCircle', bg: 'rgba(245, 158, 11, 0.1)' },
    info: { color: colors.info, icon: 'lightbulb', bg: 'rgba(59, 130, 246, 0.1)' },
    success: { color: colors.success, icon: 'checkCircle', bg: 'rgba(16, 185, 129, 0.1)' },
  };
  const config = severityConfig[alert.severity] || severityConfig.info;

  return (
    <div className="ai-fade-in" style={{
      padding: '14px 16px',
      borderRadius: 12,
      background: config.bg,
      border: `1px solid ${config.color}30`,
      display: 'flex',
      alignItems: 'flex-start',
      gap: 12,
    }}>
      <Icon name={config.icon} size={20} color={config.color} style={{ flexShrink: 0, marginTop: 2 }} />
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: colors.text, marginBottom: 4 }}>{alert.title}</div>
        <div style={{ fontSize: 13, color: colors.textSecondary }}>{alert.message}</div>
        {alert.action && (
          <Button size="sm" variant="ghost" style={{ marginTop: 10, padding: '6px 12px' }}>
            {alert.action}
          </Button>
        )}
      </div>
      {onDismiss && (
        <button
          onClick={() => onDismiss(alert)}
          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}
        >
          <Icon name="x" size={16} color={colors.textMuted} />
        </button>
      )}
    </div>
  );
};

const ChatMessage = ({ message, isUser }) => {
  const colors = useThemeColors();

  return (
    <div className="ai-fade-in" style={{
      display: 'flex',
      justifyContent: isUser ? 'flex-end' : 'flex-start',
      marginBottom: 16,
    }}>
      <div style={{
        maxWidth: '80%',
        padding: '14px 18px',
        borderRadius: isUser ? '18px 18px 4px 18px' : '18px 18px 18px 4px',
        background: isUser ? colors.primary : colors.bgElevated,
        color: isUser ? '#fff' : colors.text,
        fontSize: 14,
        lineHeight: 1.6,
        border: isUser ? 'none' : `1px solid ${colors.border}`,
      }}>
        {message.content}
      </div>
    </div>
  );
};

const TypingIndicator = () => {
  const colors = useThemeColors();
  return (
    <div style={{
      display: 'flex',
      justifyContent: 'flex-start',
      marginBottom: 16,
    }}>
      <div style={{
        padding: '14px 18px',
        borderRadius: '18px 18px 18px 4px',
        background: colors.bgElevated,
        border: `1px solid ${colors.border}`,
        display: 'flex',
        alignItems: 'center',
        gap: 4,
      }}>
        {[0, 1, 2].map(i => (
          <div key={i} className="ai-typing-dot" style={{
            width: 8,
            height: 8,
            borderRadius: 4,
            background: colors.textMuted,
          }} />
        ))}
      </div>
    </div>
  );
};

const LoadingSkeleton = ({ height = 80, className = '' }) => {
  const colors = useThemeColors();
  return (
    <div
      className={`ai-shimmer ${className}`}
      style={{
        height,
        borderRadius: 16,
        background: colors.bgCard,
        border: `1px solid ${colors.border}`,
      }}
    />
  );
};

const EmptyState = ({ icon, title, description, action, onAction }) => {
  const colors = useThemeColors();
  return (
    <div style={{
      padding: 48,
      textAlign: 'center',
      background: colors.bgCard,
      borderRadius: 16,
      border: `1px solid ${colors.border}`,
    }}>
      <div style={{
        width: 64,
        height: 64,
        borderRadius: 16,
        background: colors.isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        margin: '0 auto 20px',
      }}>
        <Icon name={icon} size={32} color={colors.textMuted} />
      </div>
      <h4 style={{ margin: '0 0 8px', fontSize: 18, fontWeight: 600, color: colors.text }}>{title}</h4>
      <p style={{ margin: '0 0 20px', fontSize: 14, color: colors.textSecondary, maxWidth: 300, marginLeft: 'auto', marginRight: 'auto' }}>{description}</p>
      {action && (
        <Button onClick={onAction} icon="arrowRight">{action}</Button>
      )}
    </div>
  );
};

// ============================================================================
// MAIN DASHBOARD COMPONENT
// ============================================================================

const AIInsightsDashboard = () => {
  const colors = useThemeColors();
  const chatContainerRef = useRef(null);

  // State
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [activeTab, setActiveTab] = useState('priorities');
  const [metrics, setMetrics] = useState(null);
  const [priorities, setPriorities] = useState({ critical: [], high: [], medium: [], quickWins: [], insights: [] });
  const [contacts, setContacts] = useState({ mustContactToday: [], shouldContactThisWeek: [], relationshipMaintenance: [] });
  const [alerts, setAlerts] = useState([]);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [conversationId, setConversationId] = useState(null);
  const [error, setError] = useState(null);

  // Inject styles
  useEffect(() => {
    injectStyles();
  }, []);

  // Load all dashboard data
  const loadData = useCallback(async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    else setLoading(true);
    setError(null);

    try {
      const [prioritiesRes, contactsRes, alertsRes, analyticsRes] = await Promise.allSettled([
        apiCall('/ai/kubi/priorities'),
        apiCall('/ai/kubi/contacts'),
        apiCall('/ai/kubi/alerts'),
        apiCall('/analytics/'),
      ]);

      // Process priorities
      if (prioritiesRes.status === 'fulfilled' && prioritiesRes.value) {
        const data = prioritiesRes.value;
        setPriorities({
          critical: data.critical || [],
          high: data.high || [],
          medium: data.medium || [],
          quickWins: data.quickWins || [],
          insights: data.insights || [],
        });
        if (data.metrics) {
          setMetrics(data.metrics);
        }
      }

      // Process contacts
      if (contactsRes.status === 'fulfilled' && contactsRes.value) {
        setContacts(contactsRes.value);
      }

      // Process alerts
      if (alertsRes.status === 'fulfilled' && alertsRes.value) {
        setAlerts(alertsRes.value.alerts || []);
      }

      // Fallback metrics from analytics
      if (!metrics && analyticsRes.status === 'fulfilled' && analyticsRes.value) {
        const data = analyticsRes.value;
        setMetrics({
          pipelineHealth: data.pipelineHealth || 'Good',
          activityLevel: data.activityLevel || 'Active',
          projectedPlacements: data.projectedPlacements || 0,
          activeCandidates: data.candidatesSaved || 0,
        });
      }

    } catch (err) {
      console.error('Failed to load AI insights:', err);
      setError('Unable to load insights. Please check your connection.');
      // Demo data for preview
      setPriorities({
        critical: [{
          id: '1',
          priority: 'critical',
          type: 'pipeline',
          title: 'Connect your ATS to see real insights',
          description: 'Configure your PCRecruiter or other ATS integration in Settings to get personalized, actionable insights.',
          action: 'Go to Settings',
          category: 'Setup',
        }],
        high: [{
          id: '2',
          priority: 'high',
          type: 'candidate',
          title: 'AI-powered recommendations coming soon',
          description: 'Once connected, Roxi will analyze your pipeline and suggest the best actions to take each day.',
          category: 'Feature',
        }],
        medium: [],
        quickWins: [],
        insights: [],
      });
    }

    setLoading(false);
    setRefreshing(false);
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Send chat message
  const sendChatMessage = async () => {
    if (!chatInput.trim() || chatLoading) return;

    const userMessage = { role: 'user', content: chatInput };
    setChatMessages(prev => [...prev, userMessage]);
    setChatInput('');
    setChatLoading(true);

    try {
      const response = await apiCall('/ai/kubi/chat', {
        method: 'POST',
        body: JSON.stringify({
          message: chatInput,
          conversationId,
        }),
      });

      setConversationId(response.conversationId);
      setChatMessages(prev => [...prev, { role: 'assistant', content: response.response }]);
    } catch (err) {
      setChatMessages(prev => [...prev, {
        role: 'assistant',
        content: 'I apologize, but I encountered an error. Please try again.'
      }]);
    }

    setChatLoading(false);
  };

  // Scroll chat to bottom
  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [chatMessages, chatLoading]);

  // Handle priority action
  const handlePriorityAction = (priority) => {
    if (priority.action === 'Go to Settings') {
      window.location.href = '/settings/integrations';
    }
  };

  // Handle contact action
  const handleContactAction = (action, contact) => {
    if (action === 'call' && contact.phone) {
      window.open(`tel:${contact.phone}`);
    } else if (action === 'email' && contact.email) {
      window.open(`mailto:${contact.email}`);
    }
  };

  // Mark priority complete
  const handlePriorityComplete = async (priority) => {
    try {
      await apiCall(`/ai/kubi/priorities/${priority.id}/complete`, { method: 'POST' });
      setPriorities(prev => ({
        ...prev,
        critical: prev.critical.filter(p => p.id !== priority.id),
        high: prev.high.filter(p => p.id !== priority.id),
        medium: prev.medium.filter(p => p.id !== priority.id),
      }));
    } catch (err) {
      console.error('Failed to mark complete:', err);
    }
  };

  // Dismiss alert
  const handleDismissAlert = async (alert) => {
    try {
      await apiCall(`/ai/kubi/alerts/${alert.id}/read`, { method: 'POST' });
      setAlerts(prev => prev.filter(a => a.id !== alert.id));
    } catch (err) {
      console.error('Failed to dismiss alert:', err);
    }
  };

  // Get all priorities as flat list
  const allPriorities = [
    ...priorities.critical.map(p => ({ ...p, priority: 'critical' })),
    ...priorities.high.map(p => ({ ...p, priority: 'high' })),
    ...priorities.medium.map(p => ({ ...p, priority: 'medium' })),
  ];

  // Get all contacts as flat list
  const allContacts = [
    ...contacts.mustContactToday?.map(c => ({ ...c, urgency: 'urgent' })) || [],
    ...contacts.shouldContactThisWeek?.map(c => ({ ...c, urgency: 'high' })) || [],
    ...contacts.relationshipMaintenance?.map(c => ({ ...c, urgency: 'normal' })) || [],
  ];

  const suggestedPrompts = [
    "What should I focus on today?",
    "Analyze my pipeline health",
    "Who needs follow-up this week?",
    "Help me with a candidate outreach",
  ];

  return (
    <div style={{ minHeight: '100vh', padding: '0 0 40px' }}>
      {/* Header */}
      <div className="ai-fade-in" style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: 28,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <div style={{
            width: 52,
            height: 52,
            borderRadius: 14,
            background: colors.gradient1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: `0 8px 32px ${colors.primaryGlow}`,
          }}>
            <Icon name="sparkles" size={28} color="#fff" />
          </div>
          <div>
            <h1 style={{ margin: 0, fontSize: 28, fontWeight: 700, color: colors.text }}>
              AI Insights
            </h1>
            <p style={{ margin: '4px 0 0', fontSize: 14, color: colors.textSecondary }}>
              Powered by Roxi • Your intelligent recruiting assistant
            </p>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <Button variant="ghost" icon="settings" onClick={() => window.location.href = '/ai-assistant/settings'}>
            Settings
          </Button>
          <Button variant="secondary" icon="refresh" loading={refreshing} onClick={() => loadData(true)}>
            {refreshing ? 'Refreshing...' : 'Refresh'}
          </Button>
        </div>
      </div>

      {/* Alerts */}
      {alerts.length > 0 && (
        <div className="ai-fade-in ai-stagger-1" style={{ marginBottom: 24, display: 'flex', flexDirection: 'column', gap: 12 }}>
          {alerts.slice(0, 3).map(alert => (
            <AlertCard key={alert.id} alert={alert} onDismiss={handleDismissAlert} />
          ))}
        </div>
      )}

      {/* Error Banner */}
      {error && (
        <div className="ai-fade-in" style={{
          marginBottom: 24,
          padding: '14px 18px',
          borderRadius: 12,
          background: 'rgba(239, 68, 68, 0.1)',
          border: '1px solid rgba(239, 68, 68, 0.2)',
          display: 'flex',
          alignItems: 'center',
          gap: 12,
        }}>
          <Icon name="alertCircle" size={20} color={colors.error} />
          <span style={{ fontSize: 14, color: colors.error, flex: 1 }}>{error}</span>
          <Button size="sm" variant="ghost" onClick={() => setError(null)}>Dismiss</Button>
        </div>
      )}

      {/* Metrics Row */}
      <div className="ai-fade-in ai-stagger-2" style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
        gap: 16,
        marginBottom: 28,
      }}>
        {loading ? (
          <>
            <LoadingSkeleton height={110} />
            <LoadingSkeleton height={110} />
            <LoadingSkeleton height={110} />
            <LoadingSkeleton height={110} />
          </>
        ) : (
          <>
            <MetricCard
              icon="activity"
              label="Pipeline Health"
              value={metrics?.pipelineHealth || 'Good'}
              subValue="Based on activity"
              color="success"
              className="ai-stagger-1"
            />
            <MetricCard
              icon="zap"
              label="Activity Level"
              value={metrics?.activityLevel || 'Active'}
              subValue="This week"
              trend={12}
              color="warning"
              className="ai-stagger-2"
            />
            <MetricCard
              icon="target"
              label="Projected Placements"
              value={metrics?.projectedPlacements || 0}
              subValue="Next 30 days"
              color="purple"
              className="ai-stagger-3"
            />
            <MetricCard
              icon="users"
              label="Active Candidates"
              value={metrics?.activeCandidates || 0}
              subValue="In pipeline"
              color="primary"
              className="ai-stagger-4"
            />
          </>
        )}
      </div>

      {/* Tab Navigation */}
      <div className="ai-fade-in ai-stagger-3" style={{
        display: 'flex',
        gap: 8,
        marginBottom: 24,
        padding: 6,
        background: colors.bgCard,
        borderRadius: 14,
        border: `1px solid ${colors.border}`,
        width: 'fit-content',
      }}>
        {[
          { id: 'priorities', label: 'Priorities', icon: 'target', count: allPriorities.length },
          { id: 'contacts', label: 'Contacts', icon: 'users', count: allContacts.length },
          { id: 'insights', label: 'Insights', icon: 'lightbulb', count: priorities.insights?.length || 0 },
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '10px 18px',
              borderRadius: 10,
              border: 'none',
              background: activeTab === tab.id ? colors.primary : 'transparent',
              color: activeTab === tab.id ? '#fff' : colors.textSecondary,
              fontSize: 14,
              fontWeight: 600,
              cursor: 'pointer',
              transition: 'all 0.2s ease',
            }}
          >
            <Icon name={tab.icon} size={16} />
            {tab.label}
            {tab.count !== undefined && tab.count > 0 && (
              <span style={{
                background: activeTab === tab.id ? 'rgba(255,255,255,0.2)' : colors.isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
                padding: '2px 8px',
                borderRadius: 10,
                fontSize: 12,
              }}>
                {tab.count}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Main Content */}
      <div className="ai-fade-in ai-stagger-4">
        {/* Priorities Tab */}
        {activeTab === 'priorities' && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 320px', gap: 24 }}>
            {/* Main Priorities */}
            <div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
                <h2 style={{ margin: 0, fontSize: 18, fontWeight: 600, color: colors.text }}>
                  Today's Priorities
                </h2>
                <span style={{ fontSize: 13, color: colors.textMuted }}>
                  {allPriorities.length} action{allPriorities.length !== 1 ? 's' : ''}
                </span>
              </div>

              {loading ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  <LoadingSkeleton height={140} />
                  <LoadingSkeleton height={120} />
                  <LoadingSkeleton height={120} />
                </div>
              ) : allPriorities.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {allPriorities.map((priority, idx) => (
                    <div key={priority.id || idx} className={`ai-fade-in ai-stagger-${Math.min(idx + 1, 5)}`}>
                      <PriorityCard
                        priority={priority}
                        onAction={handlePriorityAction}
                        onComplete={handlePriorityComplete}
                      />
                    </div>
                  ))}
                </div>
              ) : (
                <EmptyState
                  icon="checkCircle"
                  title="All caught up!"
                  description="No urgent priorities right now. Great job staying on top of your pipeline!"
                />
              )}
            </div>

            {/* Quick Wins Sidebar */}
            <div>
              <h3 style={{ margin: '0 0 16px', fontSize: 16, fontWeight: 600, color: colors.text, display: 'flex', alignItems: 'center', gap: 8 }}>
                <Icon name="zap" size={18} color={colors.warning} />
                Quick Wins
              </h3>
              {priorities.quickWins?.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {priorities.quickWins.map((win, idx) => (
                    <Card key={idx} style={{ padding: 14 }}>
                      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                        <Badge variant="warning" size="xs">{win.timeEstimate || '5 min'}</Badge>
                        <div>
                          <div style={{ fontSize: 14, fontWeight: 500, color: colors.text, marginBottom: 4 }}>{win.action || win.title}</div>
                          <div style={{ fontSize: 12, color: colors.textMuted }}>{win.impact || win.reason}</div>
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              ) : (
                <Card style={{ padding: 20, textAlign: 'center' }}>
                  <Icon name="rocket" size={24} color={colors.textMuted} style={{ marginBottom: 8 }} />
                  <div style={{ fontSize: 13, color: colors.textMuted }}>No quick wins identified yet</div>
                </Card>
              )}
            </div>
          </div>
        )}

        {/* Contacts Tab */}
        {activeTab === 'contacts' && (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
              <h2 style={{ margin: 0, fontSize: 18, fontWeight: 600, color: colors.text }}>
                Reach Out Today
              </h2>
              <span style={{ fontSize: 13, color: colors.textMuted }}>
                {allContacts.length} contact{allContacts.length !== 1 ? 's' : ''}
              </span>
            </div>

            {loading ? (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))', gap: 12 }}>
                <LoadingSkeleton height={90} />
                <LoadingSkeleton height={90} />
                <LoadingSkeleton height={90} />
              </div>
            ) : allContacts.length > 0 ? (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))', gap: 12 }}>
                {allContacts.map((contact, idx) => (
                  <div key={contact.id || idx} className={`ai-fade-in ai-stagger-${Math.min(idx + 1, 5)}`}>
                    <ContactCard contact={contact} onAction={handleContactAction} />
                  </div>
                ))}
              </div>
            ) : (
              <EmptyState
                icon="users"
                title="No contacts to reach"
                description="Connect your ATS to see AI-powered contact recommendations."
                action="Connect ATS"
                onAction={() => window.location.href = '/settings/integrations'}
              />
            )}
          </div>
        )}

        {/* Insights Tab */}
        {activeTab === 'insights' && (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
              <h2 style={{ margin: 0, fontSize: 18, fontWeight: 600, color: colors.text }}>
                AI Insights & Observations
              </h2>
            </div>

            {loading ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <LoadingSkeleton height={100} />
                <LoadingSkeleton height={100} />
              </div>
            ) : priorities.insights?.length > 0 ? (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(400px, 1fr))', gap: 16 }}>
                {priorities.insights.map((insight, idx) => (
                  <Card key={idx} style={{ padding: 20 }} className={`ai-fade-in ai-stagger-${Math.min(idx + 1, 5)}`}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14 }}>
                      <div style={{
                        width: 44,
                        height: 44,
                        borderRadius: 12,
                        background: `${colors.purple}20`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}>
                        <Icon name="lightbulb" size={22} color={colors.purple} />
                      </div>
                      <div>
                        <div style={{ fontSize: 15, fontWeight: 600, color: colors.text, marginBottom: 8 }}>
                          {insight.observation || insight.title}
                        </div>
                        <div style={{ fontSize: 13, color: colors.textSecondary, lineHeight: 1.6 }}>
                          {insight.recommendation || insight.description}
                        </div>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            ) : (
              <EmptyState
                icon="lightbulb"
                title="No insights yet"
                description="As you use Roxi more, AI will surface patterns and recommendations to improve your recruiting."
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AIInsightsDashboard;
